import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel } from 'react-responsive-carousel';
import {
  allTimezones,
  useTimezoneSelect,
} from 'react-timezone-select';
import { Box, Dialog,IconButton, Slide, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import dayjs from 'dayjs';
import { AssignModal } from 'src/components/Modal/AssignModal';
import { useAppSelector } from 'src/hooks/redux';
import { getAdmins } from 'src/store/reducers/adminsSlice';
import { assignCategories } from 'src/store/reducers/categoriesSlice';
import { getCategoryTemplates } from 'src/store/reducers/categoryTemplatesSlice';
import {
  resetCurrentStudioData,
  updateScheduleData,
} from 'src/store/reducers/currentStudioSlice';
import { getEmployees } from 'src/store/reducers/employeesSlice';
import { getSchedules } from 'src/store/reducers/schedulesSlice';
import {
  createStudios,
  getStudios,
  updateStudios,
} from "src/store/reducers/studiosSlice";
import {
  getNormalizedDaysOff,
  getNormalizedHolidays,
  getNormalizedWorkingTimes,
} from "src/utils/calendar";
import { generalStyles } from 'src/utils/generalStyles/generalStyles';
import { countCheckedServices } from 'src/utils/helpers';
import { scheduleDataCompiler } from 'src/utils/scheduleDataCompiler';
import { isAddress, isTitle } from "src/utils/validation";

import { CloseIcon } from "../../../Icons";
import {
  closeStudioDialog,
  resetStudioDialogParams,
} from "../../../store/reducers/dialogSlice";
import { closeModal, showModal } from "../../../store/reducers/modalSlice";
import {
  Calendar,
  CalendarSideBar,
  DefaultSingleSelector,
  NextSubmitButton,
} from "../..";
import { SubmitButton } from "../../Button/SubmitButton";
import { BannerSelect } from "../../ImageSelect/BannerSelect";
import { GallerySelect } from "../../ImageSelect/GallerySelect";
import { LogoStudioSelect } from "../../ImageSelect/LogoStudioSelect";
import { DefaultInput } from "../../Input/DefaultInput";
import { DefaultArraySelector } from "../../Selector/DefaultArraySelector";
import { DefaultArraySelectorItem } from "../../Selector/DefaultArraySelectorItem";
import { Steppler } from "../../Steppler/Steppler";

import { BottomBar } from './ServiceAndCategoryStep.tsx/BottomBar';
import { ServiceCategoryStep } from './ServiceAndCategoryStep.tsx';

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

enum ImagesVariable {
  Banner,
  Logo,
}

export const StudioDialog = ({ isOpen }: any) => {

  const dispatch = useDispatch();
  const sheduleTemplates = useAppSelector(state => state.schedules.schedules)

  const { studioDialogParams: params } = useSelector(
    (state: any) => state.dialog
  );

  const { categories } = useAppSelector((state: any) => state.categoryTemplates);
  const { admins } = useSelector((state: any) => state.admins);
  const { employees } = useSelector((state: any) => state.employees);
  const adminsToSelect = useMemo(() => {
    return admins.map(
      ({ id, firstName, lastName, photo, status }: any) => ({
        id,
        imageSrc: photo,
        name: firstName,
        surname: lastName,
        status,
      })
    ).filter((admin: any) => admin.status === 'active');
  },[admins])

  const employeesToSelect = employees.map(
    ({ id, firstName, lastName, photo }: any) => ({
      id,
      imageSrc: photo,
      name: firstName,
      surname: lastName,
    })
  );

  const { options } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: allTimezones,
  });

  const timeZoneOptions = useMemo(() => {
    return options.map((el: any, index: number) => ({
      ...el,
      id: index,
    }));
  },[options])

  const [activeStep, setActiveStep] = useState<number>(params.step);
  const [maxAvailableStepToClick, setMaxAvailableStepToClick] =
    useState<number>(params.maxAvailableStep);
  const formRef = useRef<any>();
  const logoRef = useRef<any>();
  const bannerRef = useRef<any>();

  // Step 1 basic
  const [images, setImages] = useState<string[]>(params.data.images);
  const [imageBanner, setImageBanner] = useState<string>(
    params.data.images[ImagesVariable.Banner]
  );
  const [logo, setLogo] = useState<string>(
    params.data.images[ImagesVariable.Logo]
  );
  const [title, setTitle] = useState<string>(params.data.title);
  const [address, setAddress] = useState<string>(params.data.address);
  const [timeZone, setTimeZone] = useState<any>(
    timeZoneOptions.find((zone: any) => zone.value === params.data.timezone)
      ?.id || ''
  );

  //Step 2 template info
  const sheduleTemplatesToSelect = useMemo(() => {
    return sheduleTemplates.map((template: any) => ({
      label: template.title,
      ...template
    }))
  },[sheduleTemplates])
  const [assignedSheduleTemplates, setAssignedSheduleTemplates] = useState<any[] | string>('')
  const [selectedTemplate, setSelectedTemplate] = useState()

  // Step 2 / 3 shedule templates
  const [workingTimes, setWorkingTimes] = useState(params.data.schedule.workingTimes);
  const [selectorId, setSelectorId] = useState();
  const [daysOff, setDaysOff] = useState(params.data.schedule.daysOff);
  const [holidays, setHolidays] = useState(params.data.schedule.holidays);

  const deletedDaysOff = useAppSelector((state: any) =>
    state.currentStudio.selectedStudioData?.daysOffToDelete
  ).map(
    (day: any) => ({
      ...day.period
    })
  );
  const patchDaysOff = useAppSelector((state: any) =>
    state.currentStudio.selectedStudioData?.daysOffToPatch
  ).map(
    (day: any) => ({
      targetPeriod: {
        from: dayjs(day.id).format('YYYY-MM-DD'),
        to: dayjs(day.id).format('YYYY-MM-DD')
      },
      period:{
        from: dayjs(day.value).format('YYYY-MM-DD'),
        to: dayjs(day.value).format('YYYY-MM-DD'),
      }
    })
  );

  // Step 3 / 4 management
  const [assignedAdmins, setAssignedAdmins] = useState<string[]>(
    params.data.admins?.filter((admin: any) => admin.status === 'active')?.map(({ id }: any) => id) || []
  );

  const [assignedEmployees, setAssignedEmployees] = useState<string[]>(
    params.data.masters?.map(({ id }: any) => id) || []
  );
  // step 4 / 5 gallery
  const imagesArray = [...params.data.images];
  const [galleryImages, setGalleryImages] = useState<any>(
    params.data.images.length > 2 ? imagesArray.splice(2) : []
  );

  const { isUpdateRequestLoading, isCreateRequestLoading } = useSelector(
    (state: any) => state.studios
  );

  // step 5 CATEGORIES
  const [selectedForTableCategories, setSelectedForTableCategories] = useState<any[]>([]);
  const [assignedToStudioCategories, setAssignedToStudioCategories] = useState<any[]>([]);
  const [assignedToStudioServices, setAssignedToStudioServices] = useState<any[]>([]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleCategoryClick = useCallback((category: any) => {
    setSelectedForTableCategories(prevCategories => {
      const exists = prevCategories.some((cat) => cat.id === category.id);
      if (exists) {
        return prevCategories.filter((cat) => cat.id !== category.id);
      } else {
        return [...prevCategories, category];
      }
    });
  }, [setSelectedForTableCategories]);

  const handlePopupCategoryClick = (category: any) => {
    setAssignedToStudioCategories(prevCategories => {
      const exists = prevCategories.some((cat) => cat.id === category.id);
      if (exists) {
        const updatedCategories = prevCategories.filter((cat) => cat.id !== category.id);
        setSelectedForTableCategories(prevSelectedCategories =>
          prevSelectedCategories.filter((cat) => cat.id !== category.id)
        );
        return updatedCategories;
      } else {
        return [...prevCategories, category];
      }
    });
  };

  const categoriesToPreview = useMemo(() => {
    const servicesTo = assignedToStudioServices.filter((cat: any) => {
      return cat.isChecked;
    }).map((cat: any) => {
      const newServices = cat.services.filter((s: any) => s.isChecked);
      const newChildren = cat.children.filter((subCat: any) => subCat.isChecked).map((subCat: any) => {
        const newServices = subCat.services.filter((el: any) => el.isChecked);

        return {
          ...subCat,
          services: newServices,
        }
      })

      return {
        ...cat,
        services: newServices,
        children: newChildren,
      }
    });
    return servicesTo;
  }, [assignedToStudioServices]);

  const handleChangeCheckService = (service: any) => {

    if (!assignedToStudioServices.length) {
      const newCheckedCategories = assignedToStudioCategories.map((category: any) => {
        const newChildren = category?.children?.map((subCat: any) => {
          const newServices = subCat.services.map((s: any) => {
            const isChecked = s.id === service.id || false;

            return {
              ...s,
              isChecked,
            }
          });

          const isChecked = newServices.some((el: any) => el.isChecked);

          return {
            ...subCat,
            isChecked,
            services: newServices,
          }
        });

        const newServices = category.services.map((s: any) => {
          const isChecked = s.id === service.id || false;

          return {
            ...s,
            isChecked,
          }
        });

        const isChecked = newChildren.some((el: any) => el.isChecked) || newServices.some((el: any) => el.isChecked);

        return {
          ...category,
          isChecked,
          children: newChildren,
          services: newServices,
        }
      });
      setAssignedToStudioServices(newCheckedCategories);

    } else {
      const newCheckedCategories = assignedToStudioServices.map((category: any) => {
        const newChildren = category?.children?.map((subCat: any) => {
          const newServices = subCat.services.map((s: any) => {
            const isChecked = s.id === service.id ? !s.isChecked : s.isChecked;

            return {
              ...s,
              isChecked,
            }
          });

          const isChecked = newServices.some((el: any) => el.isChecked);

          return {
            ...subCat,
            isChecked,
            services: newServices,
          }
        });

        const newServices = category.services.map((s: any) => {
          const isChecked = s.id === service.id ? !s.isChecked : s.isChecked;

          return {
            ...s,
            isChecked,
          }
        });

        const isChecked = newChildren.some((el: any) => el.isChecked) || newServices.some((el: any) => el.isChecked);

        return {
          ...category,
          isChecked,
          children: newChildren,
          services: newServices,
        }
      });
      setAssignedToStudioServices(newCheckedCategories);
    }
  }

  const handleChangeCheckCategory = (category: any) => {
    const getCheckedCategory = (isChecked: boolean) => ({
      ...category,
      isChecked,
      children: category.children.map((el: any) => ({
        ...el,
        isChecked,
        services: el.services.map((el: any) => ({
          ...el,
          isChecked
        }))
      })),
      services: category.services.map((el: any) => ({
        ...el,
        isChecked
      }))
    });

    setAssignedToStudioServices(prevCategories => {
      const exists = prevCategories.find((cat) => cat.id === category.id);

      if (exists) {
        const isChecked = !exists.isChecked;

        return [
          ...prevCategories.filter((cat) => cat.id !== category.id),
          getCheckedCategory(isChecked),
        ];
      } else {
        return [...prevCategories, getCheckedCategory(true)];
      }
    });
  }

  useEffect(() => {
    if (selectedTemplate) {
      //@ts-ignore
      setWorkingTimes(selectedTemplate.workingTimes);
      //@ts-ignore
      setDaysOff(selectedTemplate.daysOff);
      //@ts-ignore
      setHolidays(selectedTemplate.holidays)
    }
  }, [selectedTemplate]);

  useEffect(() => {
    // todo little hack for scroll to top after every change active step carousel
    //@ts-ignore
    window.document.querySelector('.MuiPaper-root')?.scrollTo(0, 0);
  }, [activeStep]);

  const handleChangeTitle = useCallback((newValue: string) => {
    setTitle(newValue);
  },[]);

  const handleChangeAddress = useCallback((newValue: string) => {
    setAddress(newValue);
  },[]);

  const handleChangeImageBanner = useCallback((newImage: string) => {
    setImageBanner(newImage);
  },[]);

  const handleChangeImageLogo = useCallback((newImage: string) => {
    setLogo(newImage);
  },[]);

  const handleChangeWorkingTime = useCallback((newWorkingTimes:any) => {
    setWorkingTimes(newWorkingTimes);
  },[])

  const handleChangeDaysOff = useCallback((newDaysOff:any) => {
    setDaysOff(newDaysOff);
  },[])

  const handleChangeHolidays = useCallback((newHolidays:any) => {
    setHolidays(newHolidays);
  },[])

  const handleChangeSelectorId = useCallback((newSelectorId:any) => {
    setSelectorId(newSelectorId);
  },[])

  const isSaveDraftAvailable = useMemo(() => {
    return (isTitle(title) && isAddress(address) && timeZone.length !== 0)
  }, [address, timeZone.length, title])

  const isContinueButtonAvailable = useMemo(() => {
    const isBasicInfoStepFulfilled =
      isTitle(title) &&
      isAddress(address)

    const isBasicPicturesFulfilled =
      timeZone.length !== 0 &&
      imageBanner !== undefined &&
      logo !== undefined

    const isManagementStepFulfilled = assignedAdmins.length;

    const isTemplateStepFulfilled = !!assignedSheduleTemplates;

    const isScheduleStepFulfilled = workingTimes.length;

    const isGalleryStepFulfilled = galleryImages.length;

    const isAssignedCategoriesFulFilled = categoriesToPreview.length;

    if(!params.isEdit) {
      if (activeStep !== 5) {
        return isBasicInfoStepFulfilled;
      } else {
        return (
          isBasicInfoStepFulfilled &&
          isBasicPicturesFulfilled &&
          isManagementStepFulfilled &&
          isScheduleStepFulfilled &&
          isGalleryStepFulfilled &&
          isAssignedCategoriesFulFilled
        );
      }
    }

    if(params.isEdit) {
      if (activeStep === 0) {
        return isBasicInfoStepFulfilled && isBasicPicturesFulfilled;
      } else if (activeStep === 1) {
        return isBasicInfoStepFulfilled && isBasicPicturesFulfilled && isManagementStepFulfilled;
      } else if (activeStep === 2) {
        const doTemplateStepExist = params?.data?.schedule?.workingTimes?.length === 0
          ? isTemplateStepFulfilled
          : isScheduleStepFulfilled;
        return (
          isBasicInfoStepFulfilled &&
          isBasicPicturesFulfilled &&
          isManagementStepFulfilled &&
          doTemplateStepExist
        );
      } else if (activeStep === 3) {
        const isCurrentStepGalery = params?.data?.schedule?.workingTimes?.length === 0
          ? isGalleryStepFulfilled
          : isScheduleStepFulfilled
        return (
          isBasicInfoStepFulfilled &&
          isBasicPicturesFulfilled &&
          isManagementStepFulfilled &&
          isScheduleStepFulfilled || isTemplateStepFulfilled &&
          isCurrentStepGalery
        );
      } else {
        return (
          isBasicInfoStepFulfilled &&
          isBasicPicturesFulfilled &&
          isManagementStepFulfilled &&
          isScheduleStepFulfilled || isTemplateStepFulfilled &&
          isAssignedCategoriesFulFilled &&
          isGalleryStepFulfilled
        )
      }
    }
  }, [title, address, timeZone.length, imageBanner, logo, assignedAdmins.length, assignedSheduleTemplates, workingTimes.length, galleryImages.length, categoriesToPreview.length, params.isEdit, params?.data?.schedule?.workingTimes?.length, activeStep]);

  const handleChangeStep = (nextStep: number) => {
    setActiveStep(nextStep);
  };

  const handleContinueButtonPress = useCallback(() => {
    setActiveStep((oldStep) => oldStep + 1);
    setMaxAvailableStepToClick(activeStep + 1);
  },[activeStep]);

  const resetDialogData = () => {
    setTitle('');
    setImageBanner('');
    setAddress('');
    setAssignedAdmins([]);
    setAssignedEmployees([]);
    setActiveStep(0);
    setMaxAvailableStepToClick(0);

    formRef?.current?.reset();
    logoRef?.current?.deleteImage();
    bannerRef?.current?.deleteImage();
  };

  const onClose = useCallback(() => {
    resetDialogData();
    dispatch(resetStudioDialogParams());
    dispatch(closeStudioDialog());
    dispatch(resetCurrentStudioData())
  },[dispatch]);

  const handleSaveButtonPress =  useCallback((publishSwitch: boolean) => {
    const normalizedWorkingTimes = getNormalizedWorkingTimes(workingTimes);
    const normalizedDaysOff = getNormalizedDaysOff(daysOff);
    const normalizedHolidays = getNormalizedHolidays(holidays);

    if (params.isEdit) {
      dispatch(
        updateStudios({
          title: params.data.title !== title ? title : undefined,
          address: params.data.address !== address ? address : undefined,
          ...(imageBanner || logo || galleryImages.length > 0
            ? { images: [imageBanner, logo, ...galleryImages].filter(image => image != null) }
            : undefined
          ),
          admins:
            params.data.admins !== assignedAdmins ? assignedAdmins : undefined,
          masters: assignedEmployees,
          schedule: {
            workingTimes: normalizedWorkingTimes,
            daysOff: normalizedDaysOff,
            holidays: normalizedHolidays,
          },
          currentId: params.data.id,
          onSuccess: () => {
            dispatch(
              assignCategories({
                studioId: params.data.id,
                categories: categoriesToPreview,
                onSuccess: () => {
                  dispatch(
                    showModal({
                      modalType: 'SUCCESS',
                      successText: `Draft ${title} successfully created`,
                      okButtonTitle: 'Ok, I got it',
                      onButtonClick: () => {
                        dispatch(getStudios());
                        dispatch(closeModal());
                        onClose();
                      },
                    })
                  )
                },
                onFailure: (error: any) => {
                  dispatch(
                    showModal({
                      modalType: 'DELETE',
                      title: `${error?.response?.data?.detail || error.message}`,
                      okButtonTitle: 'Ok, I got it',
                      isButtonCancel: true,
                      onButtonClick: () => {
                        dispatch(closeModal());
                      },
                    })
                  )
                },
              }));
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: 'DELETE',
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: 'Ok, I got it',
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );

      dispatch(
        updateScheduleData(
          scheduleDataCompiler({
            oldWorkTime: getNormalizedWorkingTimes(params.data.schedule.workingTimes || []),
            newWorkTime: normalizedWorkingTimes,
            oldDaysOff: getNormalizedDaysOff(params.data.schedule.daysOff),
            newDaysOff: normalizedDaysOff,
            deletedDaysOff,
            patchedDaysOff: patchDaysOff,
            oldHolidays: getNormalizedHolidays(params.data.schedule.holidays),
            newHolidays: normalizedHolidays,
            studioId: params.data.id,
          })
        ))

      dispatch(resetCurrentStudioData())
    } else {
      dispatch(
        createStudios({
          title,
          address,
          ...(imageBanner || logo || galleryImages.length > 0
            ? { images: [imageBanner, logo, ...galleryImages].filter(image => image != null) }
            : undefined
          ),
          timezone: timeZoneOptions?.[timeZone]?.value,
          admins: assignedAdmins.length > 0 ? assignedAdmins : undefined,
          masters: assignedEmployees,
          schedule: {
            workingTimes: normalizedWorkingTimes,
            daysOff: normalizedDaysOff,
            holidays: normalizedHolidays,
          },
          onSuccess: (response: any) => {
            dispatch(
              assignCategories({
                studioId: response.id,
                categories: categoriesToPreview,
                onSuccess: () => {
                  dispatch(
                    showModal({
                      modalType: 'SUCCESS',
                      successText: `Draft ${title} successfully created`,
                      okButtonTitle: 'Ok, I got it',
                      onButtonClick: () => {
                        dispatch(getStudios());
                        dispatch(closeModal());
                        onClose();
                      },
                    })
                  );
                }}));
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: 'DELETE',
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: 'Ok, I got it',
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }
  },[address, assignedAdmins, assignedEmployees, categoriesToPreview, daysOff, deletedDaysOff, dispatch, galleryImages, holidays, imageBanner, logo, onClose, params.data.address, params.data.admins, params.data.id, params.data.schedule.daysOff, params.data.schedule.holidays, params.data.schedule.workingTimes, params.data.title, params.isEdit, patchDaysOff, timeZone, timeZoneOptions, title, workingTimes]);

  const draftClick = useCallback(() => {
    handleSaveButtonPress(false)
  },[handleSaveButtonPress])

  const saveClick = useCallback(() => {
    handleSaveButtonPress(false)
  },[handleSaveButtonPress])

  const handleChangeSelectAdmin = useCallback((newArrayValue: Array<any>) => {
    setAssignedAdmins(newArrayValue);
  },[]);

  const handleChangeAssignedSheduleTemplates = useCallback((newTemplate: any) => {
    setAssignedSheduleTemplates(newTemplate)
    setSelectedTemplate(sheduleTemplatesToSelect.find((template: any) => template.id === +newTemplate))
  },[sheduleTemplatesToSelect])

  const handleChangeSelectEmployee = (newValue: Array<any>) => {
    setAssignedEmployees(newValue);
  };

  const handleChangeGalleryImages = useCallback((newImagesArray: Array<any>) => {
    const arrayImage = [imageBanner, logo, ...newImagesArray];
    setImages(arrayImage);
    setGalleryImages(newImagesArray);
  },[]);

  const handleChangeTimeZone = useCallback((newValue: any) => {
    setTimeZone(newValue);
  },[]);

  useEffect(() => {
    dispatch(getAdmins());
    dispatch(getEmployees());
    dispatch(getSchedules());
    dispatch(getCategoryTemplates());
  }, []);

  const carouselSteps = useMemo(() =>  [
    {
      title: 'Basic information',
      element:
      <Box>
        <BannerSelect
          ref={bannerRef}
          image={imageBanner}
          onImageChanged={handleChangeImageBanner}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '0px 16px 0',
              width: '761px',
              minWidth: '400px',
            }}
          >
            <LogoStudioSelect
              ref={logoRef}
              image={logo}
              onImageChanged={handleChangeImageLogo}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '40px',
                }}
              >
                    Basic information
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <form ref={formRef}>
                <DefaultInput
                  initialValue={title}
                  placeholder="name"
                  label="Name"
                  onInputValueChange={handleChangeTitle}
                  validationFn={isTitle}
                  sx={generalStyles.borderTopRightLeftRadius}
                />
                <DefaultInput
                  initialValue={address}
                  placeholder="address"
                  label="Address"
                  onInputValueChange={handleChangeAddress}
                  validationFn={isAddress}
                  sx={generalStyles.borderBottomLeftRightRadius}
                />
              </form>
            </Box>

            <Box
              sx={{
                marginTop: '48px',
              }}
            >
              <DefaultSingleSelector
                value={timeZone}
                valuesToSelect={timeZoneOptions}
                title="Time zone"
                placeholder="time zone"
                leftContainerStyle={generalStyles.leftSingleContainer}
                rightContainerStyle={generalStyles.rightSingleContainer}
                onChanged={handleChangeTimeZone}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    },
    {
      title: 'Management',
      element:
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '96px 16px 0',
            width: '761px',
            minWidth: '400px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '48px',
              }}
            >
              Admins
            </Typography>
          </Box>
          <DefaultArraySelector
            valuesArray={assignedAdmins}
            valuesToSelect={adminsToSelect}
            onChanged={handleChangeSelectAdmin}
            placeholder="admin"
            title="Admin"
            sx={generalStyles.borderRadiusAll}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(114px, 1fr))',
              gridAutoRows: 'auto',
              gridGap: '39px',
              paddingTop: '48px',
            }}
          >
            {assignedAdmins.map((id, i) => {
              const { firstName, lastName, photo } =
                admins.find((el: any) => el.id === id) || {};
              const fullName = `${firstName  } ${  lastName}`;
              return (
                <DefaultArraySelectorItem
                  key={id}
                  onRemove={() => {
                    const newArray = [
                      ...assignedAdmins.slice(0, i),
                      ...assignedAdmins.slice(i + 1),
                    ];

                    handleChangeSelectAdmin(newArray);
                  }}
                  fullName={fullName}
                  imageSrc={photo}
                  id={id}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    },
    {
      title: 'Template info',
      element:
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '96px 16px 0',
            width: '761px',
            minWidth: '400px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '48px',
              }}
            >
                      Referral shedule
            </Typography>
          </Box>
          <DefaultSingleSelector
            value={assignedSheduleTemplates}
            valuesToSelect={sheduleTemplatesToSelect}
            placeholder="referral shedule"
            onChanged={handleChangeAssignedSheduleTemplates}
            title="Referral shedule"
            leftContainerStyle={generalStyles.leftSingleContainer}
            rightContainerStyle={generalStyles.rightSingleContainer}
          />
        </Box>
      </Box>
    },
    {
      title: 'Schedule',
      element:
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '0px',
            width: '100%',
          }}
        >
          <CalendarSideBar
            workingTimes={workingTimes}
            onWorkingTimesChange={handleChangeWorkingTime}
            daysOff={daysOff}
            onDaysOffChange={handleChangeDaysOff}
            holidays={holidays}
            onHolidaysChange={handleChangeHolidays}
            periodId={selectorId}
            onChangePeriodId={handleChangeSelectorId}
            lastUpdated={params.data?.schedule?.holidays?.length === holidays?.length
              ? params.data.schedule.updatedAt
              : dayjs().toISOString()}
            period={workingTimes ? workingTimes?.[0]?.period : ''}
          />
          <Calendar
            workingTimes={workingTimes}
            holidays={holidays}
            daysOff={daysOff}
          />
        </Box>
      </Box>
    },
    {
      title: 'Categories & Services',
      element:
      <ServiceCategoryStep
        categories={categories}
        preSelectedCats={selectedForTableCategories}
        preAssignedCats={assignedToStudioCategories}
        checkedArray={assignedToStudioServices}
        onServiceCheckChange={handleChangeCheckService}
        onCategoryCheckChange={handleChangeCheckCategory}
        onCardClick={handleCategoryClick}
        onPopupCardClick={handlePopupCategoryClick}
      />
    },
    {
      title: 'Gallery',
      element:
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '96px 16px 0',
            width: '761px',
            minWidth: '400px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#080808',
              }}
            >
                      Photos
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '400',
                marginBottom: '40px',
                color: '#363636',
              }}
            >
                      Photos will appear in the Studio’s profile in clien’t app. Reccomended size 1456x728
            </Typography>
          </Box>
          <GallerySelect
            initialImagesArray={galleryImages}
            onChange={handleChangeGalleryImages}
          />
        </Box>
      </Box>
    },
  ],[address, admins, adminsToSelect, assignedAdmins, assignedSheduleTemplates, assignedToStudioCategories, assignedToStudioServices, categories, daysOff, galleryImages, handleCategoryClick, handleChangeAddress, handleChangeAssignedSheduleTemplates, handleChangeCheckService, handleChangeDaysOff, handleChangeGalleryImages, handleChangeHolidays, handleChangeImageBanner, handleChangeImageLogo, handleChangeSelectAdmin, handleChangeSelectorId, handleChangeTimeZone, handleChangeTitle, handleChangeWorkingTime, holidays, imageBanner, logo, params.data.schedule?.holidays?.length, params.data.schedule.updatedAt, selectedForTableCategories, selectorId, sheduleTemplatesToSelect, timeZone, timeZoneOptions, title, workingTimes]);

  const getSteps = useMemo(() => {
    let newSteps = carouselSteps;
    if (params.isEdit) {
      if (params.data.schedule.workingTimes.length > 0) {
        newSteps = newSteps.filter((step: any) => step.title !== 'Template info')
      }

      if (params.data.services.length > 0) {
        newSteps = newSteps.filter((step: any) => step.title !== 'Categories & Services')
      }
    }

    return newSteps
  },[carouselSteps, params])

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: '100%',
          backgroundColor: '#fafafa',
          padding: '16px 40px',
          borderBottom: '1px solid #F3F3F3',
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: '100%',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: '#F3F3F3',
              color: '#676767',
              fontSize: '26px',
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '26px',
                fontWeight: '600',
                textAlign: 'center',
              }}
            >
              {params.isEdit ? 'Edit studio' : 'Add new studio'}
            </Typography>
          </Box>

          <Box>
            {!params.isEdit &&
              <NextSubmitButton
                disabled={!isSaveDraftAvailable}
                title='Save draft'
                isLoading={
                  params.isEdit ? isUpdateRequestLoading : isCreateRequestLoading
                }
                onClick={draftClick}
                sx={{
                  marginRight: '12px',
                }}
              />
            }
            {activeStep < getSteps.length - 1 ? (
              <SubmitButton
                title="Continue"
                disabled={!isContinueButtonAvailable}
                onClick={handleContinueButtonPress}
              />
            ) : (
              <SubmitButton
                title={params.isEdit ? 'Save changes' : 'Create studio'}
                isLoading={
                  params.isEdit ? isUpdateRequestLoading : isCreateRequestLoading
                }
                disabled={!isContinueButtonAvailable}
                onClick={saveClick}
              />
            )}
          </Box>
        </Box>

        <Steppler
          steps={getSteps}
          maxAvailableStepToClick={maxAvailableStepToClick}
          activeStep={activeStep}
          onStepChange={handleChangeStep}
        />
      </Box>
      <Carousel
        selectedItem={activeStep}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        showThumbs={false}
        transitionTime={120}
      >
        {
          getSteps.map((step: {element: any, title: string}) => (
            <Box key={step.title} sx={{height: '100%'}}>
              {step.element}
            </Box>
          ))
        }
      </Carousel>
      <AssignModal
        openedFrom="prewiev"
        isOpen={isModalOpen}
        categories={categoriesToPreview}
        assignedCategories={assignedToStudioCategories}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
      {((
        activeStep === 4
          && assignedToStudioCategories.length > 0
      ) || (
        activeStep === 4
          && assignedToStudioCategories.length > 0
          && params.data.services.length === 0
          && params.data.schedule.workingTimes.length === 0
      ) || (
        activeStep === 3
          && assignedToStudioCategories.length > 0
          && params.data.services.length === 0
      )) &&
          <BottomBar
            displayData={countCheckedServices(assignedToStudioServices)}
            onButtonClick={() => setIsModalOpen(true)}
          />
      }
    </Dialog>
  )
};
